import { useState } from "react";
import { ProbeResult, REGIONS } from "../types";

interface ResultsTableProps {
  results: ProbeResult[];
  regionCount: number;
  regionsDone: number;
  selectedRegions?: string[];
}

function latencyColor(ms: number | null): string {
  if (ms === null) return "#98A1AB";
  if (ms < 100) return "#39D98A";
  if (ms < 300) return "#FDDD48";
  return "#FF5C5C";
}

function latencyBg(ms: number | null): string {
  if (ms === null) return "transparent";
  if (ms < 100) return "rgba(57,217,138,0.05)";
  if (ms < 300) return "rgba(253,221,72,0.05)";
  return "rgba(255,92,92,0.05)";
}

function formatMs(ms: number | null): string {
  if (ms === null) return "—";
  return `${ms}ms`;
}

const COLUMN_TIPS: Record<string, string> = {
  DNS: "Time to resolve the hostname to an IP address",
  TLS: "Time to complete the TLS handshake",
  TTFB: "Time to first byte — from request sent to first byte of response received",
  "Total RTT": "Full round-trip time from request start to response complete",
  Cache: "CDN cache status returned by the origin for this region",
};

const CACHE_COLOR: Record<string, string> = {
  HIT: "#39D98A",
  MISS: "#FDDD48",
  BYPASS: "#FF8C00",
  DYNAMIC: "#FF8C00",
  STALE: "#FDDD48",
  EXPIRED: "#FDDD48",
  UNKNOWN: "#98A1AB",
};

const CACHE_BG: Record<string, string> = {
  HIT: "rgba(57,217,138,0.12)",
  MISS: "rgba(253,221,72,0.12)",
  BYPASS: "rgba(255,140,0,0.12)",
  DYNAMIC: "rgba(255,140,0,0.12)",
  STALE: "rgba(253,221,72,0.12)",
  EXPIRED: "rgba(253,221,72,0.12)",
  UNKNOWN: "rgba(152,161,171,0.1)",
};

function TooltipTh({ label, align }: { label: string; align: "left" | "right" }) {
  const [visible, setVisible] = useState(false);
  const tip = COLUMN_TIPS[label];

  return (
    <th
      style={{ ...thStyle(align), position: "relative", cursor: tip ? "default" : undefined }}
      onMouseEnter={() => tip && setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {label}{tip && <span style={{ marginLeft: 3, opacity: 0.4, fontSize: 10 }}>?</span>}
      {visible && tip && (
        <div style={{
          position: "absolute",
          bottom: "calc(100% + 6px)",
          right: align === "right" ? 0 : undefined,
          left: align === "left" ? 0 : undefined,
          background: "#121212",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 3,
          padding: "6px 10px",
          fontSize: 12,
          color: "#EFF2F5",
          whiteSpace: "nowrap",
          zIndex: 10,
          pointerEvents: "none",
          fontWeight: 400,
          textTransform: "none",
          letterSpacing: 0,
        }}>
          {tip}
        </div>
      )}
    </th>
  );
}

export default function ResultsTable({ results, regionCount, regionsDone, selectedRegions }: ResultsTableProps) {
  const regions = selectedRegions && selectedRegions.length > 0 ? selectedRegions : Object.keys(REGIONS);

  const arrived = regions
    .filter((r) => results.some((res) => res.region === r))
    .sort((a, b) => {
      const aMs = results.find((r) => r.region === a)?.total_ms ?? Infinity;
      const bMs = results.find((r) => r.region === b)?.total_ms ?? Infinity;
      return aMs - bMs;
    });
  const pending = regions.filter((r) => !results.some((res) => res.region === r));
  const orderedRegions = [...arrived, ...pending];

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <div style={{ marginBottom: 10, fontSize: 13, color: "#98A1AB" }}>
        {regionsDone} / {regionCount} regions complete
        {regionsDone < regionCount && (
          <span style={{ marginLeft: 8, display: "inline-block", animation: "spin 1s linear infinite" }}>⟳</span>
        )}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
            <th style={thStyle("left")}>Region</th>
            <th style={thStyle("right")}>Status</th>
            <TooltipTh label="Cache" align="right" />
            <TooltipTh label="DNS" align="right" />
            <TooltipTh label="TLS" align="right" />
            <TooltipTh label="TTFB" align="right" />
            <TooltipTh label="Total RTT" align="right" />
          </tr>
        </thead>
        <tbody>
          {orderedRegions.map((region) => {
            const result = results.find((r) => r.region === region);
            const pending = !result;
            const total = result?.total_ms ?? null;

            return (
              <tr
                key={region}
                style={{
                  borderBottom: "1px solid rgba(255,255,255,0.05)",
                  background: pending ? "transparent" : latencyBg(total),
                }}
              >
                <td style={{ ...tdStyle, fontWeight: 500, color: "#EFF2F5" }}>
                  {REGIONS[region] ?? region}
                  {result?.resolved_ip && (
                    <div style={{ fontSize: 11, fontWeight: 400, color: "#98A1AB", marginTop: 2, fontFamily: "monospace" }}>
                      {result.resolved_ip}
                    </div>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  {pending ? (
                    <span style={{ color: "#98A1AB" }}>waiting…</span>
                  ) : result?.error ? (
                    <span
                      title={result.error}
                      style={{
                        color: "#FF5C5C", fontSize: 12,
                        maxWidth: 120, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap",
                        display: "inline-block",
                      }}
                    >
                      {result.error.slice(0, 30)}
                    </span>
                  ) : (
                    <span style={{ color: "#39D98A" }}>
                      {result?.http_status != null ? result.http_status : "OK"}
                    </span>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  {pending || !result?.cache_status ? (
                    <span style={{ color: "#98A1AB" }}>—</span>
                  ) : (
                    <span style={{
                      display: "inline-block",
                      fontSize: 11, fontWeight: 700,
                      color: CACHE_COLOR[result.cache_status] ?? CACHE_COLOR.UNKNOWN,
                      background: CACHE_BG[result.cache_status] ?? CACHE_BG.UNKNOWN,
                      padding: "2px 7px", borderRadius: 3,
                    }}>
                      {result.cache_status}
                    </span>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: "right", color: "#98A1AB" }}>
                  {formatMs(result?.dns_ms ?? null)}
                </td>
                <td style={{ ...tdStyle, textAlign: "right", color: "#98A1AB" }}>
                  {formatMs(result?.tls_ms ?? null)}
                </td>
                <td style={{ ...tdStyle, textAlign: "right", color: "#98A1AB" }}>
                  {formatMs(result?.ttfb_ms ?? null)}
                </td>
                <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, color: latencyColor(total) }}>
                  {formatMs(total)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function thStyle(align: "left" | "right"): React.CSSProperties {
  return {
    padding: "10px 12px", textAlign: align,
    fontSize: 11, fontWeight: 700,
    color: "#98A1AB",
    textTransform: "uppercase", letterSpacing: "0.06em",
  };
}

const tdStyle: React.CSSProperties = { padding: "11px 12px", fontSize: 14 };
